/* Copyright (c) 2020-2021 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Box, Loader } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import FlexContainer from "~/components/Styled/FlexContainer";
import { LOCAL_STORAGE } from "~/constants/localStorage";
import { UserProvider } from "~/context/User/User";
import { useAuth0 } from "./auth";

type LoginProps = {
  children: React.ReactNode;
};

function Login({ children }: LoginProps) {
  const {
    isAuthenticated,
    isLoading,
    loginWithRedirect,
    getIdTokenClaims,
  } = useAuth0();
  const [ready, setReady] = React.useState(false);

  const { showError } = useShowError();

  const getTokenFromAuth = React.useCallback(async () => {
    try {
      const getToken = await getIdTokenClaims();
      const token = getToken?.__raw;
      localStorage.setItem(LOCAL_STORAGE.token, token);
      localStorage.removeItem(LOCAL_STORAGE.refreshingToken);
      setReady(true);
    } catch (e) {
      showError(e.response);
    }
  }, [getIdTokenClaims, showError]);

  React.useEffect(() => {
    !isLoading && isAuthenticated && getTokenFromAuth();
  }, [getTokenFromAuth, isAuthenticated, isLoading]);

  React.useEffect(() => {
    if (!isAuthenticated && !isLoading) {
      loginWithRedirect({
        appState: {
          returnTo: window.location.pathname,
        },
      });
    }
  }, [loginWithRedirect, isAuthenticated, isLoading]);

  return !isLoading && isAuthenticated && ready ? (
    <UserProvider>{children}</UserProvider>
  ) : (
    <Box css={{ flex: "1" }}>
      <FlexContainer>
        <Loader inContent />
      </FlexContainer>
    </Box>
  );
}

export default Login;
